/**
 * The Auction Calendar's own API client.
 *
 * DELIBERATELY NOT THE SHARED axios INSTANCE. That instance has an interceptor
 * which keys off '/admin/' in the URL and attaches tenant headers, and it
 * resolves a tenant id from the hostname. A calendar subscriber has no tenant,
 * and calendar.yourfirstparcel.com resolves to none, so every request through it
 * would either carry a meaningless header or fail to resolve at all.
 *
 * SEPARATE TOKEN, SEPARATE KEY. The subscriber token is signed with
 * CALENDAR_JWT_SECRET, which is a different secret from JWT_SECRET on purpose:
 * sharing one would let a master admin token authenticate as a subscriber and
 * the reverse. Storing it under its own key keeps a subscriber logging in on the
 * same browser from clobbering an admin session, and the reverse.
 */

const TOKEN_KEY = 'calendarToken';

const API_BASE = `${process.env.REACT_APP_API_URL || '/api'}`.replace(/\/+$/, '');

export function getCalendarToken() {
  try {
    return window.localStorage.getItem(TOKEN_KEY) || '';
  } catch {
    return '';
  }
}

export function setCalendarToken(token) {
  try {
    if (token) window.localStorage.setItem(TOKEN_KEY, token);
    else window.localStorage.removeItem(TOKEN_KEY);
  } catch {
    // A browser with storage blocked can still use the app for this session.
  }
}

export function clearCalendarToken() {
  setCalendarToken('');
}

/**
 * One request.
 *
 * Throws an Error carrying the server's own message, because every message this
 * API produces is written to be read by a subscriber rather than by a developer.
 * A 401 clears the token so the app falls back to the login screen instead of
 * looping on a credential that is no longer good.
 */
export async function calendarApi(path, options = {}) {
  const token = getCalendarToken();

  const response = await fetch(`${API_BASE}/calendar${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(options.headers || {})
    }
  });

  let data = null;
  try {
    data = await response.json();
  } catch {
    data = null;
  }

  if (response.status === 401 || response.status === 403) {
    clearCalendarToken();
    const error = new Error((data && data.error) || 'Please sign in again.');
    error.unauthorized = true;
    throw error;
  }

  if (!response.ok) {
    throw new Error((data && data.error) || 'Something went wrong. Please try again.');
  }

  return data;
}
